import {
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  LoadingButton,
  Popconfirm,
  Stack,
  toast,
  Typography,
} from "@altimateai/lego";
import { FilterValue, SavedFilter } from "./types";
import { FilterTag } from ".";
import { useEffect, useRef } from "react";
import { useMutation } from "@tanstack/react-query";
import { deleteFilter } from "@/helpers/apis";
import { useAppState } from "@/modules/app/useAppContext";
import { FilterCondition, TableHeaderType } from "../tables/types";
import { formatFilterCondition } from "../tables/utils/filterConditions";
import useActiveFilters from "../tables/hooks/useActiveFilters";

interface Props {
  filter: SavedFilter;
  selectedFilter?: string;
  refetch: () => void;
  onEdit?: (filter: SavedFilter) => void;
  headers?: TableHeaderType[];
  /** Called after a filter is applied, so the parent can close the drawer. */
  onApplied?: () => void;
}
const SavedFilterComponent = ({
  filter,
  selectedFilter,
  refetch,
  onEdit,
  headers,
  onApplied,
}: Props) => {
  const ref = useRef<HTMLLIElement | null>(null);
  const isSelected = filter.id.toString() === selectedFilter;
  const { currencySymbol } = useAppState();
  const { applyFilters } = useActiveFilters(headers);

  const handleApply = () => {
    applyFilters(
      Object.entries(filter.data).reduce(
        (acc, [key, value]) => {
          if ((value as FilterValue).condition) {
            acc[key] = value as { values: FilterCondition["value"] };
          } else {
            // legacy saved filter data format
            acc[key] = { values: value as string | string[] };
          }
          return acc;
        },
        {} as Record<string, { values: FilterCondition["value"] }>
      ),
      filter.id
    );
    onApplied?.();
  };

  const { mutate, isPending: isLoading } = useMutation({
    mutationFn: deleteFilter,
    onSuccess: () => {
      refetch();
      toast({
        description: "Filter deleted successfully",
      });
    },
    onError: error => {
      toast({
        description: (error as Error | undefined)?.message || "Failed to delete filter",
        variant: "destructive",
      });
    },
  });

  useEffect(() => {
    if (isSelected) {
      ref.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [isSelected]);

  const onConfirm = () => {
    mutate(filter.id);
  };

  const handleEdit = () => {
    if (!onEdit) return;
    onEdit(filter);
  };

  return (
    <li ref={ref} className="al-mb-4">
      <Card className={isSelected ? "!al-bg-bg-brand" : ""}>
        <CardHeader>
          <Typography>{filter.name}</Typography>
        </CardHeader>
        <CardContent>
          {headers ? (
            // New format with operators when headers are provided
            <Stack wrap>
              <Typography size="sm">Where: </Typography>
              {Object.entries(filter.data).map(([searchKey, value], index) => {
                // Handle both old format (direct values) and new format (nested object)
                const actualValue = (value as FilterValue)?.condition
                  ? (value as { values: string | string[] }).values
                  : (value as string | string[]);

                const condition = headers
                  ? formatFilterCondition(searchKey, actualValue, headers, currencySymbol)
                  : null;
                if (!condition) return null;

                return (
                  <Stack key={searchKey} align="center" gap="sm">
                    {index > 0 && <Typography size="sm">AND</Typography>}
                    <Typography size="xs" className="al-text-gray-800 al-text-small">
                      {condition}
                    </Typography>
                  </Stack>
                );
              })}
            </Stack>
          ) : (
            // Fallback to original tag format when headers not provided
            // can be removed once all the table filters are migrated to new filters
            <Stack className="flex-wrap">
              {Object.entries(filter.data).map(([key, value]) => {
                if (Array.isArray(value)) {
                  return value.map((val, idx) => {
                    let filterStr: string;
                    if (
                      typeof val === "object" &&
                      val !== null &&
                      "condition" in val &&
                      "values" in val
                    ) {
                      filterStr = String(val.values);
                    } else {
                      filterStr = String(val);
                    }
                    return (
                      <FilterTag
                        key={`${key}-${idx}`}
                        label={key}
                        filterStr={filterStr}
                        searchKey=""
                        onClose={() => {}}
                        disableClose
                        bgColor="light"
                      />
                    );
                  });
                }
                let filterStrValue: string;
                if (
                  typeof value === "object" &&
                  value !== null &&
                  "condition" in value &&
                  "values" in value
                ) {
                  filterStrValue = String(value.values);
                } else {
                  filterStrValue = String(value);
                }
                return (
                  <FilterTag
                    key={key}
                    label={key}
                    filterStr={filterStrValue}
                    searchKey=""
                    onClose={() => {}}
                    bgColor="light"
                    disableClose
                  />
                );
              })}
            </Stack>
          )}
        </CardContent>
        <CardFooter>
          <Stack justify="between" className="al-w-full">
            <Popconfirm
              title="Delete the filter"
              description="Are you sure to delete this filter?"
              onConfirm={onConfirm}
              okText="Yes"
              cancelText="No"
            >
              <LoadingButton
                loading={isLoading}
                size="sm"
                variant="destructive"
                aria-label={`Delete ${filter.name}`}
              >
                Delete
              </LoadingButton>
            </Popconfirm>

            <Stack>
              {onEdit && (
                <Button size="sm" variant="outline" onClick={handleEdit}>
                  Edit
                </Button>
              )}
              <LoadingButton loading={isLoading} size="sm" onClick={handleApply}>
                Apply filters
              </LoadingButton>
            </Stack>
          </Stack>
        </CardFooter>
      </Card>
    </li>
  );
};

export default SavedFilterComponent;
